import { Controller } from "@hotwired/stimulus";

import Mutex from "../mutex"

let mutexMapsLoading = new Mutex(200);

export default class extends Controller {
  static targets = ["container"];
  static values = {
    apiKey: String,
    center: Array,
    zoom: Number,
    markers: Array,
    controls: Array,
    mapType: String,
    clusterer: Boolean,
    customOptions: Object,
    initialMarkers: Array,
  };

  connect() {
    this.map = null;
    this.clusterer = null;
    this.placemarks = [];

    this.mapContainer = this.element;
    this.initialMarkers = this.markersValue;

    mutexMapsLoading.run(async () => { await this.initializeMap() })
  }

  disconnect() {
    if (this.map) {
      this.map.destroy();
    }
  }

  async initializeMap() {
    try {
      await this.loadYandexMapsAPI();
      this.createMap();
      this.setListenerToSelectedMarkerId();
    } catch (error) {
      console.error("Failed to initialize Yandex Map:", error);
    }
  }

  loadYandexMapsAPI() {
    return new Promise((resolve, reject) => {
      if (typeof ymaps !== "undefined") {
        resolve();
        return;
      }

      const lang = document.getElementsByTagName('html')[0].lang;
      const country = {ru: "RU", en: "GB"}[lang] ?? "RU";
      const locale = `${lang}_${country}`;

      const script = document.createElement("script");
      script.src = `https://api-maps.yandex.ru/2.1/?apikey=${this.apiKeyValue}&lang=${locale}`;

      script.onload = () => {
        resolve();
      };

      script.onerror = () => {
        console.error("Failed to load Yandex Maps API");
        reject(new Error("Failed to load Yandex Maps API"));
      };

      document.head.appendChild(script);
    });
  }

  createMap() {
    ymaps.ready(() => {
      const mapOptions = this.getMapOptions();

      try {
        this.map = new ymaps.Map(this.mapContainer, mapOptions);

        this.element.yandexMap = this.map;
        this.element.yandexMapController = this;

        this.addMarkers();
        this.setupEventListeners();
      } catch (error) {
        console.error("Error creating map:", error);
      }
    });
  }

  getMapOptions() {
    const baseOptions = {
      center: this.centerValue || [55.7558, 37.6176],
      zoom: this.zoomValue || 10,
      type: this.mapTypeValue || "yandex#map",
      controls: this.controlsValue || ["zoomControl", "geolocationControl"],
    };

    const finalOptions = { ...baseOptions, ...this.customOptionsValue };
    return finalOptions;
  }

  addMarkers() {
    if (!this.markersValue || this.markersValue.length === 0) {
      return;
    }

    if (this.clustererValue) {
      this.createClusterer();
    }

    this.markersValue.forEach((markerData, index) => {
      const placemark = this.createPlacemark(markerData);

      if (this.clustererValue && this.clusterer) {
        this.placemarks.push(placemark);
      } else {
        this.map.geoObjects.add(placemark);
      }
    });

    if (this.clustererValue && this.clusterer && this.placemarks.length > 0) {
      this.clusterer.add(this.placemarks);
      this.map.geoObjects.add(this.clusterer);
    }
  }

  createClusterer() {
    this.clusterer = new ymaps.Clusterer({
      preset: "islands#blueClusterIcons",
      groupByCoordinates: false,
      clusterDisableClickZoom: true,
      clusterHideIconOnBalloonOpen: false,
      geoObjectHideIconOnBalloonOpen: false,
    });
  }

  createPlacemark(markerData) {
    const placemarkOptions = this.getPlacemarkOptions(markerData);

    return new ymaps.Placemark(
      markerData.coordinates,
      {
        balloonContent: markerData.content || markerData.title,
        hintContent: markerData.title,
      },
      placemarkOptions
    );
  }

  getPlacemarkOptions(markerData) {
    if (markerData.icon && markerData.icon.startsWith("/")) {
      return {
        iconLayout: markerData.icon_layout || "default#image",
        iconImageHref: markerData.icon,
        iconImageSize: markerData.icon_size || [34, 40],
        iconImageOffset: [-17, -40],
      };
    } else {
      return {
        preset: markerData.icon || "islands#blueDotIcon",
        iconColor: markerData.color || "#1e40af",
      };
    }
  }

  setupEventListeners() {
    if (!this.map) return;

    this.map.events.add("click", (e) => {
      const coords = e.get("coords");
      this.dispatch("click", { coords });
    });

    window.addEventListener("resize", () => {
      if (this.map && this.map.container) {
        this.map.container.fitToViewport();
      }
    });
  }

  setCenter(coordinates) {
    if (this.map) {
      this.map.setCenter(coordinates);
    }
  }

  setZoom(zoom) {
    if (this.map) {
      this.map.setZoom(zoom);
    }
  }

  addMarker(markerData) {
    if (!this.map) return;

    const placemark = this.createPlacemark(markerData);

    if (this.clustererValue && this.clusterer) {
      this.clusterer.add(placemark);
    } else {
      this.map.geoObjects.add(placemark);
    }

    // Track the placemark
    this.placemarks.push(placemark);

    return placemark;
  }

  removeMarker(placemark) {
    if (!this.map) return;

    if (this.clustererValue && this.clusterer) {
      this.clusterer.remove(placemark);
    } else {
      this.map.geoObjects.remove(placemark);
    }
  }

  clearMarkers() {
    if (!this.map) return;

    if (this.clustererValue && this.clusterer) {
      this.clusterer.removeAll();
    } else {
      this.map.geoObjects.removeAll();
    }

    // Clear the placemarks array
    this.placemarks = [];
  }

  fitToMarkers() {
    if (!this.map) return;

    const geoObjects =
      this.clustererValue && this.clusterer
        ? this.clusterer.getGeoObjects()
        : this.map.geoObjects;

    if (geoObjects.getLength() === 0) return;

    const bounds = geoObjects.getBounds();
    if (bounds) {
      this.map.setBounds(bounds, { checkZoomRange: true });
    }
  }

  getMap() {
    return this.map;
  }

  getClusterer() {
    return this.clusterer;
  }

  // maybe its not best solution, but it works smh
  setListenerToSelectedMarkerId() {
    const selectElement = document.querySelector(
      "[data-select-id='campus_filter']"
    );
    if (selectElement) {
      selectElement.addEventListener("change", (e) => {
        this.clearMarkers();

        const value = parseInt(selectElement.getAttribute("data-select-selected-value"));

        if (value !== 0) {
          this.markersValue = this.initialMarkers.filter((marker) => marker.campus_id === value);
          this.setCenter(this.markersValue[0].coordinates);
          this.setZoom(12);
        } else {
          this.markersValue = [...this.initialMarkers];
          this.setCenter(this.centerValue);
          this.setZoom(this.zoomValue);
        }

        this.addMarkers();
      });
    }
  }

  dispatch(name, detail = {}) {
    const event = new CustomEvent(`yandex-map:${name}`, {
      bubbles: true,
      detail: { map: this.map, ...detail },
    });
    this.element.dispatchEvent(event);
  }
}
